import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import axios from 'axios';
import { dv } from '../i18n/dv.ts';

type ErrorKind = 'expired' | 'generic' | 'mismatch' | null;

export function ResetPassword(): JSX.Element {
  const { token } = useParams({ from: '/reset-password/$token' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<ErrorKind>(null);
  const [pending, setPending] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('mismatch');
      return;
    }
    setPending(true);
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 400 || status === 404) {
        setError('expired');
      } else {
        setError('generic');
      }
    } finally {
      setPending(false);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 space-y-4">
        <h1 className="font-serif text-3xl tracking-tight">{dv.auth.passwordChanged}</h1>
        <Link to="/login" className="btn-primary">
          {dv.auth.login}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-12">
      <h1 className="font-serif text-3xl tracking-tight mb-8">{dv.auth.resetPassword}</h1>
      <form className="space-y-3" onSubmit={submit}>
        <input
          className="input"
          type="password"
          dir="ltr"
          aria-label={dv.auth.newPassword}
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <input
          className="input"
          type="password"
          dir="ltr"
          aria-label={dv.auth.confirmPassword}
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
        {error === 'mismatch' && (
          <p className="text-sm text-destructive" role="alert">
            {dv.auth.passwordMismatch}
          </p>
        )}
        {error === 'expired' && (
          <p className="text-sm text-destructive" role="alert">
            {dv.auth.resetLinkExpired}{' '}
            <Link to="/forgot-password" className="underline underline-offset-4 hover:opacity-80">
              {dv.auth.sendResetLink}
            </Link>
          </p>
        )}
        {error === 'generic' && (
          <p className="text-sm text-destructive" role="alert">
            {dv.common.error}
          </p>
        )}
        <button
          type="submit"
          disabled={pending}
          className="btn-primary w-full"
        >
          {pending ? dv.common.loading : dv.auth.resetPassword}
        </button>
      </form>
    </div>
  );
}
